import React, { useEffect, useState } from 'react';
import { XIcon, MapPinIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from './ui/Button';
interface ConsultationFormProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ConsultationForm({
  isOpen,
  onClose
}: ConsultationFormProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Reset form state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitted(false);
    }
  }, [isOpen]);
  // Fire Google Ads conversion when thank you screen loads
  useEffect(() => {
    if (isSubmitted && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-830901894'
      });
    }
  }, [isSubmitted]);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.open('https://calendly.com/be_clinical/30min', '_blank');
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-18">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-espresso/80 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" data-id="element-19" />

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200" data-id="element-20">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-6 right-6 text-muted-foreground hover:text-espresso transition-colors p-1 flex-shrink-0 z-10" aria-label="Close modal" data-id="element-21">
          <XIcon className="w-6 h-6" data-id="element-22" />
        </button>

        {isSubmitted /* Thank You Screen */ ? <div className="px-8 py-16 flex flex-col items-center text-center" data-id="element-23">
            <CheckCircleIcon className="w-16 h-16 text-sage mb-6" data-id="element-24" />
            <h2 className="font-serif text-3xl md:text-4xl text-espresso mb-4" data-id="element-25">
              Thank You!
            </h2>
            <p className="font-sans text-base text-muted-foreground max-w-md mb-2" data-id="element-26">
              Your consultation request has been received. A Calendly scheduling
              page has opened in a new tab.
            </p>
            <p className="font-sans text-sm text-muted-foreground max-w-md mb-8" data-id="element-27">
              If it didn't open,{' '}
              <a href="https://calendly.com/be_clinical/30min" target="_blank" rel="noopener noreferrer" className="text-gold underline hover:text-gold-dark" data-id="element-28">
                Schedule a free consultation
              </a>
              .
            </p>
            <Button onClick={onClose} variant="primary" data-id="element-29">
              Close
            </Button>
          </div> /* Form */ : <>
            {/* Header */}
            <div className="flex items-start justify-between px-8 pt-8 pb-6" data-id="element-30">
              <div data-id="element-31">
                <h2 className="font-serif text-3xl md:text-4xl text-espresso" data-id="element-32">
                  Book Consultation
                </h2>
                <div className="mt-3 space-y-0.5" data-id="element-33">
                  <div className="flex items-center gap-2" data-id="element-34">
                    <MapPinIcon className="w-5 h-5 text-muted-foreground flex-shrink-0" data-id="element-35" />
                    <p className="font-sans text-base text-muted-foreground" data-id="element-36">
                      Be Clinical Med Spa in San Ramon, CA
                    </p>
                  </div>
                  <p className="font-sans text-sm text-muted-foreground pl-7" data-id="element-37">
                    Off of 680 Freeway and Crow Canyon Road
                  </p>
                </div>
              </div>
            </div>

            {/* Form Body */}
            <div className="px-8 pb-8 overflow-y-auto" data-id="element-38">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-39">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-40">
                  <div className="space-y-2" data-id="element-41">
                    <label htmlFor="firstName" className="block font-sans text-sm font-medium text-espresso" data-id="element-42">
                      First Name *
                    </label>
                    <input type="text" id="firstName" required className="w-full px-4 py-3 bg-white border border-divider rounded-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow" data-id="element-43" />
                  </div>
                  <div className="space-y-2" data-id="element-44">
                    <label htmlFor="lastName" className="block font-sans text-sm font-medium text-espresso" data-id="element-45">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" required className="w-full px-4 py-3 bg-white border border-divider rounded-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow" data-id="element-46" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-47">
                  <label htmlFor="email" className="block font-sans text-sm font-medium text-espresso" data-id="element-48">
                    Email Address *
                  </label>
                  <input type="email" id="email" required className="w-full px-4 py-3 bg-white border border-divider rounded-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow" data-id="element-49" />
                </div>

                <div className="space-y-2" data-id="element-50">
                  <label htmlFor="phone" className="block font-sans text-sm font-medium text-espresso" data-id="element-51">
                    Phone Number *
                  </label>
                  <input type="tel" id="phone" required className="w-full px-4 py-3 bg-white border border-divider rounded-sm focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent transition-shadow" data-id="element-52" />
                </div>

                {/* Consents */}
                <div className="space-y-5 pt-4 border-t border-divider" data-id="element-53">
                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-54">
                    <div className="flex-shrink-0 mt-0.5" data-id="element-55">
                      <input type="checkbox" required className="w-5 h-5 rounded-sm border-divider text-gold focus:ring-gold cursor-pointer accent-gold" data-id="element-56" />
                    </div>
                    <span className="font-sans text-xs text-muted-foreground leading-relaxed group-hover:text-espresso transition-colors" data-id="element-57">
                      By checking this box, I consent to receive non-marketing
                      text messages from Be Clinical about appointment
                      confirmations, service updates, and scheduling. Message
                      frequency varies. Message & data rates may apply. Reply
                      STOP to unsubscribe at any time. *
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-58">
                    <div className="flex-shrink-0 mt-0.5" data-id="element-59">
                      <input type="checkbox" className="w-5 h-5 rounded-sm border-divider text-gold focus:ring-gold cursor-pointer accent-gold" data-id="element-60" />
                    </div>
                    <span className="font-sans text-xs text-muted-foreground leading-relaxed group-hover:text-espresso transition-colors" data-id="element-61">
                      By checking this box, I consent to receive marketing text
                      messages from Be Clinical including special offers,
                      discounts, and promotional updates at the phone number
                      provided. Frequency may vary. Message & data rates may
                      apply. Reply STOP to unsubscribe at any time.
                    </span>
                  </label>
                </div>

                {/* Footer Actions */}
                <div className="pt-6 pb-2" data-id="element-62">
                  <Button type="submit" className="w-full" data-id="element-63">
                    Continue to Scheduling
                  </Button>
                </div>
              </form>
            </div>
          </>}
      </div>
    </div>;
}